import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, RefreshCw, FileText, ClipboardList, BarChart2, DollarSign, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import DashboardKPIs from '../components/dashboard/DashboardKPIs';
import DashboardMeta from '../components/dashboard/DashboardMeta';
import DashboardCharts from '../components/dashboard/DashboardCharts';
import DashboardPedidosSemana from '../components/dashboard/DashboardPedidosSemana';
import ClientesSemCompraCard from '../components/dashboard/ClientesSemCompraCard';
import ClientesEmRiscoCard from '../components/dashboard/ClientesEmRiscoCard';
import CurvaABCWidget from '../components/dashboard/CurvaABCWidget';
import ComissaoProjetadaCard from '../components/dashboard/ComissaoProjetadaCard';
import AlertasCard from '../components/dashboard/AlertasCard';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const getSemanaAtual = () => {
  const hoje = new Date();
  const domingo = new Date(hoje); domingo.setDate(hoje.getDate() - hoje.getDay());
  const sabado = new Date(domingo); sabado.setDate(domingo.getDate() + 6);
  const toStr = (d) => d.toISOString().split('T')[0];
  return { inicio: toStr(domingo), fim: toStr(sabado), hoje: toStr(hoje) };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [stats, setStats] = useState({
    tonelagem_implantada: 0, comissao_prevista: 0,
    tonelagem_faturada: 0,   comissao_realizada: 0,
    pedidos_mes_valor: 0,    faturado_mes_valor: 0,
    comissao_mes: 0,         comissoes_a_receber: 0,
    faturamento_mensal: Array(12).fill(0),
  });
  const [metasList, setMetasList] = useState([]);
  const [pedidosSemana, setPedidosSemana] = useState([]);
  const [todosPedidos, setTodosPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [diasAbertos, setDiasAbertos] = useState({});
  const [clientes, setClientes] = useState([]);
  const [filterRepresentada, setFilterRepresentada] = useState('');
  const filterMounted = useRef(false);
  const semana = getSemanaAtual();

  const representadasUnicas = useMemo(() =>
    [...new Set(todosPedidos.map(p => p.representada_nome).filter(Boolean))].sort()
  , [todosPedidos]);

  const pedidosFiltrados = filterRepresentada
    ? todosPedidos.filter(p => p.representada_nome === filterRepresentada)
    : todosPedidos;

  const pedidosSemanaFiltrados = filterRepresentada
    ? pedidosSemana.filter(p => p.representada_nome === filterRepresentada)
    : pedidosSemana;

  const metaGlobal = useMemo(() => {
    const mesAtualStr = (new Date().getMonth() + 1).toString();
    const metasMes = metasList.filter(m => m.mes === mesAtualStr);
    if (!filterRepresentada) return metasMes.reduce((a, m) => a + parseFloat(m.valor_ton || 0), 0);
    const clienteIds = new Set(
      todosPedidos.filter(p => p.representada_nome === filterRepresentada).map(p => p.cliente_id).filter(Boolean)
    );
    return metasMes.filter(m => clienteIds.has(m.cliente_id)).reduce((a, m) => a + parseFloat(m.valor_ton || 0), 0);
  }, [metasList, filterRepresentada, todosPedidos]);

  const faturamentoPorMes = useMemo(() => {
    const arr = stats.faturamento_mensal || Array(12).fill(0);
    return MESES.map((m, i) => ({ mes: m, valor: arr[i] || 0 }));
  }, [stats.faturamento_mensal]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!filterMounted.current) { filterMounted.current = true; return; }
    loadStats(filterRepresentada);
  }, [filterRepresentada]); // eslint-disable-line

  const loadStats = async (representadaNome = '') => {
    try {
      const params = representadaNome ? { representada_nome: representadaNome } : {};
      const res = await api.get('/dashboard/stats', { params });
      setStats(res.data);
    } catch { toast.error('Erro ao filtrar indicadores'); }
  };

  const loadAll = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const statsParams = filterRepresentada ? { params: { representada_nome: filterRepresentada } } : {};
      const [statsRes, metasRes, pedidosRes, clientesRes] = await Promise.all([
        api.get('/dashboard/stats', statsParams),
        api.get('/metas'),
        api.get('/pedidos'),
        api.get('/clientes'),
      ]);
      setStats(statsRes.data);
      setClientes(clientesRes.data || []);
      setMetasList(metasRes.data || []);
      const todos = pedidosRes.data || [];
      setTodosPedidos(todos);
      const semanaFiltrados = todos.filter(p => {
        if (!p.data_entrega) return false;
        const dataEntrega = p.data_entrega.substring(0, 10);
        return dataEntrega >= semana.inicio && dataEntrega <= semana.fim && p.status !== 'CANCELADO' && p.status !== 'NF_EMITIDA';
      }).sort((a, b) => a.data_entrega?.localeCompare(b.data_entrega));
      setPedidosSemana(semanaFiltrados);
      setDiasAbertos({ [semana.hoje]: true });
    } catch { toast.error('Erro ao sincronizar indicadores'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const toggleDia = (dia) => setDiasAbertos(prev => ({ ...prev, [dia]: !prev[dia] }));

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 animate-pulse">Sincronizando indicadores...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen font-sans antialiased text-slate-800">

      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Dashboard</h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">
            {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isAdmin && (
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg">
              <Lock size={11} className="text-slate-400" />
              <span className="text-xs font-bold text-slate-400">Somente Leitura</span>
            </div>
          )}
          {representadasUnicas.length > 0 && (
            <select value={filterRepresentada} onChange={e => setFilterRepresentada(e.target.value)}
              className="border border-slate-200 rounded-lg bg-white px-3 h-8 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A3D73] transition-all cursor-pointer shadow-sm">
              <option value="">Todas representadas</option>
              {representadasUnicas.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <button onClick={() => loadAll(true)} disabled={refreshing}
            className="flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 rounded-lg hover:border-[#0A3D73] hover:text-[#0A3D73] transition-all shadow-sm">
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      <AlertasCard />

      <DashboardKPIs
        stats={stats}
        pedidosFiltrados={pedidosFiltrados}
        clientes={clientes}
        onNavigate={navigate}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <DashboardMeta
          stats={stats}
          metaGlobal={metaGlobal}
          isAdmin={isAdmin}
          onNavigate={navigate}
        />
        <DashboardCharts faturamentoPorMes={faturamentoPorMes} />
      </div>

      <DashboardPedidosSemana
        pedidosSemanaFiltrados={pedidosSemanaFiltrados}
        semana={semana}
        diasAbertos={diasAbertos}
        onToggleDia={toggleDia}
        clientes={clientes}
        onNavigate={navigate}
      />

      {/* ── INTELIGÊNCIA COMERCIAL ── */}
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Inteligência Comercial</p>
      <div className="flex flex-col gap-4 mb-5">
        <ComissaoProjetadaCard />
        <ClientesSemCompraCard />
        <ClientesEmRiscoCard />
        <CurvaABCWidget />
      </div>

      {/* ── ATALHOS ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pedidos',    icon: FileText,      link: '/pedidos' },
          { label: 'Orçamentos', icon: ClipboardList, link: '/orcamentos' },
          { label: 'Relatórios', icon: BarChart2,     link: '/relatorios' },
          { label: 'Comissões',  icon: DollarSign,    link: '/comissoes' },
        ].map((item, i) => (
          <button key={i} onClick={() => navigate(item.link)}
            className="flex items-center justify-between gap-2 p-3.5 bg-white border border-slate-200 text-xs font-black uppercase tracking-wide text-slate-600 rounded-xl hover:bg-[#0A3D73] hover:text-white hover:border-[#0A3D73] transition-all shadow-sm group">
            <div className="flex items-center gap-2">
              <item.icon size={14} className="flex-shrink-0" />
              {item.label}
            </div>
            <ChevronRight size={11} className="text-slate-300 group-hover:text-white" />
          </button>
        ))}
      </div>

    </div>
  );
};

export default Dashboard;
